import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    inject,
    OnInit,
    QueryList,
    ViewChildren
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavbarService } from "../../../../../layouts/tenant-interactive-layout/navbar.service";
import { DOCUMENT, NgTemplateOutlet } from "@angular/common";
import { MatProgressBar } from "@angular/material/progress-bar";
import {
    CdkDrag,
    CdkDragDrop,
    CdkDragEnd,
    CdkDragHandle,
    CdkDragMove,
    CdkDropList,
    moveItemInArray
} from "@angular/cdk/drag-drop";
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from "@angular/cdk/overlay";
import { CdkScrollable, ScrollDispatcher } from "@angular/cdk/scrolling";
import { catchError, concatMap, EMPTY, exhaustMap, filter, finalize, interval, Observable, of, timer } from "rxjs";

import {
    AudioPlayTrack,
    AudioPlayerService,
    AudioState,
    PlayIconComponent,
    ToggleComponent
} from "@sxm-content-creators/tools-ds";

import { LazyImageDirective } from "../../../../../directives/lazy-image/lazy-image.directive";
import { ContentHeightDirective } from "../../../../../directives/content-height/content-height.directive";
import { FlipService } from "../../../../../shared/services/flip.service";
import { SnackbarService } from "../../../../../components/snackbar/snackbar.service";
import { TagComponent } from "../../../../../components/tag/tag.component";
import { BadgeResponse } from "../../../../../admin/themes/models/theme.model";
import { SubmissionFreeformResponse } from "../../../models/submission-freeform.model";
import { SubmitterContactResponse } from "../../../models/submitter.model";
import { SubmissionFreeformApiService } from "../../../services/submission-freeform.api.service";
import { SubmitterApiService } from "../../../services/submitter.api.service";
import { HostApiService } from "../host.api.service";
import { SplitterPipe } from "../../../../../shared/pipes/splitter.pipe";

// Chips shown before the rest collapse into a `+N` badge. Fewer at `medium`, where the chips
// share a row with the play control and the submitter instead of having a row to themselves.
const MAX_VISIBLE_SUBMISSION_TAGS = 4;
const MAX_VISIBLE_SUBMISSION_TAGS_MEDIUM = 2;

const TAG_CATEGORY_KEYS = [
    "tagCategoryA",
    "tagCategoryB",
    "tagCategoryC",
    "tagCategoryD",
    "tagCategoryE",
    "tagCategoryF"
] as const satisfies readonly (keyof SubmissionFreeformResponse)[];

// Shown in place of a submitter's name or location when we have no value for it, so both
// rows always render and the column stays aligned from card to card.
const NOT_AVAILABLE = "N/A";

// Host view only surfaces badges that have an image, so `badges` is pre-filtered to guarantee it.
export type BadgeWithImage = BadgeResponse & { imageUrl: string };

@Component({
    selector: "app-host-freeform",
    templateUrl: "./host-freeform.component.html",
    styleUrl: "./host-freeform.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    providers: [FlipService],
    imports: [
        MatProgressBar,
        TagComponent,
        CdkDropList,
        CdkDrag,
        CdkDragHandle,
        CdkScrollable,
        CdkOverlayOrigin,
        CdkConnectedOverlay,
        ToggleComponent,
        LazyImageDirective,
        ContentHeightDirective,
        PlayIconComponent,
        SplitterPipe,
        NgTemplateOutlet
    ]
})
export class HostFreeformComponent implements OnInit {
    private readonly hostApiService = inject(HostApiService);
    private readonly submissionFreeformService = inject(SubmissionFreeformApiService);
    private readonly submitterApiService = inject(SubmitterApiService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly snackbarService = inject(SnackbarService);
    private readonly audioPlayerService = inject(AudioPlayerService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly scrollDispatcher = inject(ScrollDispatcher);
    private readonly document = inject(DOCUMENT);
    protected readonly navbarService = inject(NavbarService);
    private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
    private readonly flip = inject(FlipService);
    private readonly interactiveId$ = this.navbarService.selectedInteractiveIds$.pipe(filter(ids => ids.length > 0));

    protected badgeDelayMs: number = 30;

    // How often the list is re-fetched to pick up changes made from another tab/host.
    private static readonly POLL_INTERVAL_MS = 7500;
    // How long after a write the list is left alone. Reads can be served by a database replica
    // that lags the write, so a list fetched right after a save may still be in the old order.
    private static readonly WRITE_SETTLE_MS = 8000;

    // True once the first list has rendered, so the initial paint never animates.
    private hasLoaded = false;
    // Guards that pause polling so a refresh never lands mid-interaction: while a
    // row is being dragged, or an optimistic write is in flight.
    private isDragging = false;
    private pendingWrites = 0;
    // Bumped by every local change (a drag, a write). A fetch started before the latest bump
    // returns the list from before that change, and applying it would snap rows back.
    private localChangeVersion = 0;
    // Polls are skipped until this time (ms since epoch), set when a write finishes.
    private settleUntil = 0;
    // Raised for the duration of a drag (plus a tick) so the click it ends with is ignored.
    private suppressRowClick = false;

    @ViewChildren(LazyImageDirective) private readonly badgeDirectives!: QueryList<LazyImageDirective>;

    submissions: SubmissionFreeformResponse[] = [];
    badges: BadgeWithImage[] = [];
    showBadges = false;
    hoveredBadgeZoneId: string | null = null;
    draggingBadgeId: string | null = null;
    tenantLogoUrl: string | null = null;
    hostViewWallpaperUrl: string | null = null;
    readonly expandedNotes = new Set<string>();
    readonly animatingBadges = new Set<string>();
    readonly audioTracks = new Map<string, AudioPlayTrack>();
    private readonly loadedAudioPaths = new Map<string, string | null>();
    // Resolved submitter contact details, keyed by submitterContactId.
    private readonly submitters = new Map<string, SubmitterContactResponse>();
    // Every id we've asked for, including ones the service resolved to nothing. Contact records
    // have a TTL of ~30 days, so an older submission's id legitimately never resolves — without
    // remembering the misses, the poll would re-request them every tick, forever.
    private readonly requestedSubmitterIds = new Set<string>();
    protected openSubmissionOverlayId: string | null = null;
    // Covers the "Text submission" trigger: top edges aligned, or bottom edges when there's
    // no room below.
    protected readonly submissionOverlayPositions: ConnectedPosition[] = [
        { originX: "start", originY: "top", overlayX: "start", overlayY: "top" },
        { originX: "start", originY: "bottom", overlayX: "start", overlayY: "bottom" }
    ];

    ngOnInit(): void {
        this.interactiveId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.getHostViewData());
        this.setupAudioListeners();
        this.setupPolling();
        this.scrollDispatcher
            .scrolled()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.closeSubmissionOverlay());
    }

    getHostViewData(): void {
        const tenantId = this.navbarService.tenantId();
        const interactiveId = this.navbarService.interactiveId();
        if (!tenantId || !interactiveId) {
            this.showError();
            return;
        }

        this.hostApiService.getHostViewData(tenantId, interactiveId).subscribe({
            next: ({ submissions, theme }) => {
                this.submissions = submissions;
                this.hasLoaded = true;
                this.loadAudioUrls(submissions);
                this.loadSubmitters(submissions);
                // Host view only surfaces active badges that have an image: an imageless
                // or inactive badge can't be shown in the panel or on a row, so it's
                // dropped here. When none remain, the panel + per-item badge zone are
                // hidden (see template `badges.length` gates) while reordering stays.
                this.badges = (theme.freeformConfig?.badgesConfig ?? [])
                    .filter((badge): badge is BadgeWithImage => !!badge.imageUrl && badge.responseIsActive)
                    .sort((a, b) => a.displayOrder - b.displayOrder);
                this.tenantLogoUrl = theme.themeConfig?.tenantLogoUrl ?? null;
                this.hostViewWallpaperUrl = theme.themeConfig?.hostViewWallpaperUrl ?? null;
                this.changeDetectorRef.markForCheck();
            },
            error: () => this.showError()
        });
    }

    isPlaying(): boolean {
        return this.audioPlayerService.state === AudioState.PLAYING;
    }

    formatDuration(seconds: number): string {
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        return `${m}:${s.toString().padStart(2, "0")}`;
    }

    get nowPlayingId(): string {
        return this.audioPlayerService.tracks?.[this.audioPlayerService.index]?.id ?? "";
    }

    onRowClick(submission: SubmissionFreeformResponse, event: Event): void {
        // A drag ends on mouseup, which also produces a click on the row it started in.
        // That click is the tail of a reorder, not a deliberate row click, so ignore it.
        if (this.suppressRowClick) return;

        const target = event.target as HTMLElement;
        if (target.closest("button, a, input, [cdkDragHandle], .badge-zone, tools-toggle, tools-play-icon")) {
            return;
        }

        // On keyboard activation, stop Space from scrolling the page.
        if (event instanceof KeyboardEvent) {
            event.preventDefault();
        }

        // Clicking anywhere on the row makes it the active submission for the broadcast view.
        // For an audio row the play below emits through the same path and re-highlights, which
        // is a no-op by then; doing it here too covers rows with no audio to play.
        this.setHighlighted(submission);

        if (!submission.hasAudio) {
            // Opens rather than toggles: the overlay's outside-click handler has already closed
            // it by the time this runs, so clicking the open row again keeps it open.
            if (this.fullText(submission)) this.openSubmissionOverlay(submission.id);
            return;
        }

        if (this.isPlaying() && this.nowPlayingId === submission.id) {
            this.audioPlayerService.emitAudioPause();
            return;
        }

        this.audioPlayerService.emitAudioPlayList({
            trackId: submission.id,
            playlistId: this.navbarService.interactiveId() ?? ""
        });
    }

    private setupAudioListeners(): void {
        this.audioPlayerService
            .getAudioPlayList()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(event => {
                const track = this.audioTracks.get(event.trackId);
                if (track) {
                    this.audioPlayerService.playList(0, [track]);
                    this.audioPlayerService.play();
                }

                // Playing a submission also makes it the active one for the broadcast view.
                // Hooking the play event rather than the click covers the play button as well
                // as a row click. Pausing deliberately leaves the highlight where it is.
                const submission = this.submissions.find(s => s.id === event.trackId);
                if (submission) {
                    this.setHighlighted(submission);
                }
            });

        // We do a 100 poll to update audio progress since host is onPush change detection
        this.audioPlayerService
            .getAudioPlay()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                interval(100)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => this.changeDetectorRef.markForCheck());
            });

        this.audioPlayerService
            .getAudioPause()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.changeDetectorRef.markForCheck();
            });

        this.audioPlayerService
            .getAudioStop()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.changeDetectorRef.markForCheck();
            });
    }

    private loadAudioUrls(submissions: SubmissionFreeformResponse[]): void {
        submissions
            .filter(
                submission =>
                    submission.hasAudio && this.loadedAudioPaths.get(submission.id) !== submission.audioSubmissionPath
            )
            .forEach(submission => this.fetchAudio(submission));
        this.changeDetectorRef.markForCheck();
    }

    /**
     * Fetch (or refetch) a submission's audio from its current audioSubmissionPath. Only called
     * for a track that's missing or stale, so a replacement here means we already had a
     * now-outdated track cached: stop it if it's the one currently playing rather than let the
     * host keep listening to stale audio, and don't autoplay the replacement - the host can
     * press play again once it's loaded.
     */
    private fetchAudio(submission: SubmissionFreeformResponse): void {
        const isReplacement = this.loadedAudioPaths.has(submission.id);
        this.loadedAudioPaths.set(submission.id, submission.audioSubmissionPath);

        if (isReplacement && this.isPlaying() && this.nowPlayingId === submission.id) {
            this.audioPlayerService.emitAudioStop();
        }

        const staleUrl = this.audioTracks.get(submission.id)?.audioUrl;
        if (staleUrl) URL.revokeObjectURL(staleUrl);

        this.audioTracks.set(submission.id, {
            id: submission.id,
            title: submission.shortDescription ?? "",
            subtitle: "",
            audioUrl: "",
            duration: 0
        });

        this.submissionFreeformService
            .getAudioFile(submission.id)
            .pipe(catchError(() => of(null)))
            .subscribe(audioFile => {
                if (audioFile) {
                    this.audioTracks.set(submission.id, {
                        ...this.audioTracks.get(submission.id)!,
                        audioUrl: audioFile.url,
                        duration: audioFile.duration
                    });
                    this.changeDetectorRef.markForCheck();
                }
            });
    }

    /**
     * Resolve the submitter behind each submission, skipping any id we've already asked about.
     * A lookup that fails is deliberately not remembered, so the next poll retries it; one that
     * simply has no record is, and reads as "N/A" from then on.
     */
    private loadSubmitters(submissions: SubmissionFreeformResponse[]): void {
        const ids = [
            ...new Set(
                submissions
                    .map(submission => submission.submitterContactId)
                    .filter((id): id is string => !!id && !this.requestedSubmitterIds.has(id))
            )
        ];
        if (!ids.length) return;

        ids.forEach(id => this.requestedSubmitterIds.add(id));

        this.submitterApiService
            .getSubmitters(ids)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ items }) => {
                    // The response is sparse and unordered, so match on submitterContactId
                    // rather than position; ids with no record are simply absent.
                    items.forEach(item => this.submitters.set(item.submitterContactId, item));
                    this.changeDetectorRef.markForCheck();
                },
                // No snackbar: this view polls every few seconds, so a failed lookup would
                // otherwise toast on every tick. Falling back to "N/A" is quieter and honest.
                error: () => ids.forEach(id => this.requestedSubmitterIds.delete(id))
            });
    }

    private submitterFor(submission: SubmissionFreeformResponse): SubmitterContactResponse | undefined {
        const id = submission.submitterContactId;
        return id ? this.submitters.get(id) : undefined;
    }

    /** The submitter's name, or "N/A" when they're anonymous or their contact record has lapsed. */
    protected submitterName(submission: SubmissionFreeformResponse): string {
        return this.submitterFor(submission)?.name || NOT_AVAILABLE;
    }

    /** "City, State" where known, falling back to the country code, then to "N/A". */
    protected submitterLocation(submission: SubmissionFreeformResponse): string {
        const submitter = this.submitterFor(submission);
        if (!submitter) return NOT_AVAILABLE;

        const cityState = [submitter.city, submitter.state].filter(Boolean).join(", ");
        return cityState || submitter.countryCode || NOT_AVAILABLE;
    }

    onShowBadgesChange(checked: boolean): void {
        this.showBadges = checked;
        if (checked) {
            const startDelayMs: number = 180;

            // Small delay so the grid has opened before badges start fading in
            this.badgeDirectives.forEach((dir, i) => dir.addLoadedClass(startDelayMs + i * this.badgeDelayMs));
        } else {
            // Reset immediately on close so images are hidden before next open
            this.badgeDirectives.forEach(dir => dir.reset());
        }
        this.changeDetectorRef.markForCheck();
    }

    onListDragStarted(): void {
        this.isDragging = true;
        this.localChangeVersion++;
        // A drag ends in a mouseup that also produces a click on the row it started in, and
        // that click must not read as a deliberate row click. The guard goes up here rather
        // than on drag end because the CDK emits `ended` only once the 250ms drop animation
        // has finished — well after the click has already been dispatched.
        this.suppressRowClick = true;
    }

    onListDragEnded(): void {
        this.isDragging = false;
        // Deferred so the guard also outlives a click dispatched after `ended`, which is the
        // ordering when the drop animation is skipped (no transition, or a zero-distance drop).
        setTimeout(() => (this.suppressRowClick = false));
    }

    onDrop(event: CdkDragDrop<SubmissionFreeformResponse[]>): void {
        if (event.previousIndex === event.currentIndex) return;

        const draggedSubmission = this.submissions[event.previousIndex];
        const newPriority = event.currentIndex + 1; // event is zero based, priority is 1 based

        // The drag already animates the reorder natively via the CDK, so mutate directly.
        moveItemInArray(this.submissions, event.previousIndex, event.currentIndex);
        this.updatePriority(draggedSubmission.id, newPriority);
    }

    moveToTop(submission: SubmissionFreeformResponse, index: number): void {
        if (index === 0) return;

        // Unlike a drag, this reorder has no native animation, so FLIP it into place.
        this.reorderList(() => moveItemInArray(this.submissions, index, 0));
        this.updatePriority(submission.id, 1);
    }

    onRemove(submission: SubmissionFreeformResponse): void {
        this.refreshSubmissions(this.submissionFreeformService.removePriority(submission.id));
    }

    onMarkUsed(submission: SubmissionFreeformResponse, isUsed: boolean): void {
        submission.isUsed = isUsed;
        this.changeDetectorRef.markForCheck();

        this.pausePollingDuring(this.submissionFreeformService.updateIsUsed(submission.id, isUsed))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                error: () => {
                    submission.isUsed = !isUsed;
                    this.changeDetectorRef.markForCheck();
                    this.showError();
                }
            });
    }

    /**
     * Make `submission` the active one for the broadcast view. Only one submission per
     * interactive is highlighted at a time, so this clears the previous one locally to
     * match what the service does, rather than waiting for a refetch to reveal it.
     *
     * There is deliberately no toggle-off: the highlight marks what the broadcast view is
     * currently on, so it moves between rows rather than being switched off. Clearing it
     * when a submission leaves the host view is the service's job (CREATORS-30141).
     */
    private setHighlighted(submission: SubmissionFreeformResponse): void {
        if (submission.isHighlighted) return;

        const previouslyHighlighted = this.submissions.filter(s => s.isHighlighted);
        previouslyHighlighted.forEach(s => (s.isHighlighted = false));
        submission.isHighlighted = true;
        this.changeDetectorRef.markForCheck();

        this.pausePollingDuring(this.submissionFreeformService.updateIsHighlighted(submission.id, true))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                error: () => {
                    submission.isHighlighted = false;
                    previouslyHighlighted.forEach(s => (s.isHighlighted = true));
                    this.changeDetectorRef.markForCheck();
                    this.showError();
                }
            });
    }

    private updatePriority(id: string, priority: number): void {
        // Already reordered locally, so the confirming refetch can wait out replica lag.
        this.refreshSubmissions(this.submissionFreeformService.updatePriority(id, priority), {
            delayMs: HostFreeformComponent.WRITE_SETTLE_MS
        });
    }

    private refreshSubmissions(
        refresh$: Observable<SubmissionFreeformResponse | void>,
        { delayMs = 0 }: { delayMs?: number } = {}
    ): void {
        const source$ = this.pausePollingDuring(
            refresh$.pipe(
                concatMap(() => (delayMs ? timer(delayMs) : of(null))),
                concatMap(() => this.submissionFreeformService.getPrioritized(this.navbarService.interactiveId()))
            )
        );
        // Read after `pausePollingDuring` has counted this write, so only a later change invalidates it.
        // A stale list is dropped; the local list already reflects the newer change, and a later
        // refresh or poll brings the server's.
        const version = this.localChangeVersion;
        source$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: submissions => {
                if (version === this.localChangeVersion) this.applyRefresh(submissions);
            },
            error: () => this.showError()
        });
    }

    /**
     * Poll the prioritized list every POLL_INTERVAL_MS so a change saved in
     * another tab shows up here. Skips a tick while the user is mid-interaction
     * (dragging, an in-flight write, or a FLIP animation).
     */
    private setupPolling(): void {
        if (!this.navbarService.tenantId() || !this.navbarService.interactiveId()) return;

        interval(HostFreeformComponent.POLL_INTERVAL_MS)
            .pipe(
                filter(() => !this.isBusy),
                exhaustMap(() => {
                    const version = this.localChangeVersion;
                    return this.submissionFreeformService.getPrioritized(this.navbarService.interactiveId()).pipe(
                        catchError(() => EMPTY),
                        // The idle check above ran at request time; drop a response that lands
                        // mid-interaction or after a local change it doesn't know about.
                        filter(() => !this.isBusy && version === this.localChangeVersion)
                    );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(submissions => this.applyPoll(submissions));
    }

    private get isBusy(): boolean {
        return this.isDragging || this.flip.isAnimating || this.pendingWrites > 0 || Date.now() < this.settleUntil;
    }

    /** Result of a poll from another tab: only touch the DOM when something visibly changed. */
    private applyPoll(next: SubmissionFreeformResponse[]): void {
        if (!this.hasListDiff(this.submissions, next)) return;

        const firstLoad = !this.hasLoaded;
        this.commitSubmissions(next, !firstLoad);
        if (!firstLoad) this.triggerSyncPulse();
    }

    /**
     * Result of our own save: always adopt the server list and pulse the sync
     * bar to confirm the save landed. Only FLIP when the visible order/content
     * actually changed (a drag already animated itself; a priority-only renumber
     * has nothing to move).
     */
    private applyRefresh(next: SubmissionFreeformResponse[]): void {
        const changed = this.hasListDiff(this.submissions, next);
        this.commitSubmissions(next, changed);
        this.triggerSyncPulse();
    }

    private commitSubmissions(next: SubmissionFreeformResponse[], animate: boolean): void {
        // Badges assigned/changed elsewhere should replay the same pop animation
        // as a local drop when they sync in.
        const poppedBadgeIds = this.changedBadgeIds(this.submissions, next);

        const apply = (): void => {
            this.submissions = next;
            this.hasLoaded = true;
            this.loadAudioUrls(next);
            this.loadSubmitters(next);
        };

        if (animate && this.hasLoaded) {
            this.reorderList(apply);
        } else {
            apply();
            this.changeDetectorRef.markForCheck();
        }

        poppedBadgeIds.forEach(id => this.triggerBadgeAnimation(id));
    }

    /**
     * FLIP-animate a mutation to the submission list via the shared service. The
     * service updates the data (mutate) and, when it animates, renders the new
     * positions synchronously; when it doesn't animate we do a normal CD pass.
     */
    private reorderList(mutate: () => void): void {
        const animated = this.flip.animate({
            container: this.host.nativeElement,
            itemSelector: ".submission-item",
            key: el => el.dataset["submissionId"] ?? null,
            mutate,
            render: () => this.changeDetectorRef.detectChanges()
        });

        if (!animated) {
            this.changeDetectorRef.markForCheck();
        }
    }

    /** Ids of submissions whose visibly-assigned badge was added or changed between two fetches. */
    private changedBadgeIds(prev: SubmissionFreeformResponse[], next: SubmissionFreeformResponse[]): string[] {
        const prevRatingById = new Map(prev.map(s => [s.id, this.getAssignedBadge(s)?.id ?? null]));
        return next
            .filter(s => {
                const assignedId = this.getAssignedBadge(s)?.id ?? null;
                return assignedId !== null && prevRatingById.has(s.id) && prevRatingById.get(s.id) !== assignedId;
            })
            .map(s => s.id);
    }

    /**
     * Serialize the ordered list to compare fetches. Array order captures the
     * reordering itself and membership; the fields capture display-affecting
     * edits (mark used, badge, score, notes, tags, a replaced audioSubmissionPath…).
     * `priority` and timestamps are deliberately excluded — they back the order
     * rather than being shown, so a pure renumber after our own reorder is not
     * treated as a change.
     */
    private listSignature(list: SubmissionFreeformResponse[]): string {
        return JSON.stringify(
            list.map(s => [
                s.id,
                s.isUsed,
                s.isHighlighted,
                s.responseRating,
                s.responseHideBadgeImage,
                s.score,
                s.notes,
                s.shortDescription,
                s.cleanedText,
                s.textSubmission,
                s.hasAudio,
                s.audioSubmissionPath,
                s.state,
                s.submitterContactId,
                s.tagCategoryA,
                s.tagCategoryB,
                s.tagCategoryC,
                s.tagCategoryD,
                s.tagCategoryE,
                s.tagCategoryF
            ])
        );
    }

    private hasListDiff(a: SubmissionFreeformResponse[], b: SubmissionFreeformResponse[]): boolean {
        return this.listSignature(a) !== this.listSignature(b);
    }

    /** Fill the success progress line across the top of the badge bar on each sync. */
    private triggerSyncPulse(): void {
        const el = this.host.nativeElement.querySelector<HTMLElement>(".sync-sweep");
        if (!el) return;

        el.classList.remove("active");
        el.getBoundingClientRect(); // force reflow so the animation restarts on every sync
        el.classList.add("active");
    }

    /** Pause polling while `source$` is in flight, so a refresh never clobbers an unsaved optimistic change. */
    private pausePollingDuring<T>(source$: Observable<T>): Observable<T> {
        this.pendingWrites++;
        this.localChangeVersion++;
        return source$.pipe(
            finalize(() => {
                this.pendingWrites = Math.max(0, this.pendingWrites - 1);
                this.settleUntil = Date.now() + HostFreeformComponent.WRITE_SETTLE_MS;
            })
        );
    }

    protected fullText(submission: SubmissionFreeformResponse): string | null {
        return submission.cleanedText || submission.textSubmission || null;
    }

    protected openSubmissionOverlay(id: string): void {
        this.openSubmissionOverlayId = id;
        this.changeDetectorRef.markForCheck();
    }

    protected closeSubmissionOverlay(): void {
        this.openSubmissionOverlayId = null;
        this.changeDetectorRef.markForCheck();
    }

    toggleNotes(id: string): void {
        if (this.expandedNotes.has(id)) {
            this.expandedNotes.delete(id);
        } else {
            this.expandedNotes.add(id);
        }
        this.changeDetectorRef.markForCheck();
    }

    protected getBadgeById(id: string): BadgeWithImage | undefined {
        return this.badges.find(b => b.id === id);
    }

    // From the host's perspective, a hidden badge image means "nothing assigned"
    protected getAssignedBadge(submission: SubmissionFreeformResponse): BadgeWithImage | undefined {
        if (!submission.responseRating || submission.responseHideBadgeImage) return undefined;
        return this.getBadgeById(submission.responseRating);
    }

    onBadgeDragStarted(badgeId: string): void {
        this.draggingBadgeId = badgeId;
        this.changeDetectorRef.markForCheck();
    }

    onBadgeDragMoved(event: CdkDragMove<BadgeResponse>): void {
        const submissionId = this.submissionIdAtPoint(event.pointerPosition.x, event.pointerPosition.y);
        if (submissionId === this.hoveredBadgeZoneId) return;

        this.hoveredBadgeZoneId = submissionId;
        this.changeDetectorRef.markForCheck();
    }

    onBadgeDragEnded(event: CdkDragEnd<BadgeResponse>, badge: BadgeResponse): void {
        const submissionId = this.submissionIdAtPoint(event.dropPoint.x, event.dropPoint.y);
        event.source.reset();
        this.hoveredBadgeZoneId = null;
        this.draggingBadgeId = null;

        const submission = this.submissions.find(s => s.id === submissionId);
        if (!submission) {
            this.changeDetectorRef.markForCheck();
            return;
        }

        this.triggerBadgeAnimation(submission.id);
        this.assignBadge(submission, badge);
    }

    private submissionIdAtPoint(x: number, y: number): string | null {
        const element = this.document.elementFromPoint(x, y);
        // Each submission item has a data-submission-id attribute in the DOM.
        return element?.closest<HTMLElement>(".submission-item")?.dataset["submissionId"] ?? null;
    }

    private triggerBadgeAnimation(submissionId: string): void {
        this.animatingBadges.add(submissionId);
        this.changeDetectorRef.markForCheck();
        setTimeout(() => {
            this.animatingBadges.delete(submissionId);
            this.changeDetectorRef.markForCheck();
        }, 600);
    }

    private assignBadge(submission: SubmissionFreeformResponse, badge: BadgeResponse): void {
        const previousResponseRating = submission.responseRating ?? null;
        const previousHideBadgeImage = submission.responseHideBadgeImage;
        // A drag-drop is a full override, so un-hide along with assigning the new rating.
        submission.responseRating = badge.id;
        submission.responseHideBadgeImage = false;
        this.changeDetectorRef.markForCheck();

        this.pausePollingDuring(this.submissionFreeformService.updateHostResponse(submission.id, badge.id))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                error: () => {
                    submission.responseRating = previousResponseRating;
                    submission.responseHideBadgeImage = previousHideBadgeImage;
                    this.changeDetectorRef.markForCheck();
                    this.showError();
                }
            });
    }

    protected readonly maxVisibleSubmissionTags = MAX_VISIBLE_SUBMISSION_TAGS;
    protected readonly maxVisibleSubmissionTagsMedium = MAX_VISIBLE_SUBMISSION_TAGS_MEDIUM;

    getTags(submission: SubmissionFreeformResponse): string[] {
        return TAG_CATEGORY_KEYS.map(key => submission[key]).filter((tag): tag is string => !!tag);
    }

    showError(): void {
        this.snackbarService.show("Uh oh, something's gone wrong.", "error");
    }
}
